import React, { useState, useEffect } from 'react';
import {
  BookOpen,
  X,
  Calendar,
  Clock,
  ArrowRight,
  BrainCircuit,
  BarChart3,
  Megaphone,
  Rocket,
} from 'lucide-react';
import blogData from '../data/blog.json';

const iconStyle = { display: 'inline', verticalAlign: 'middle', marginRight: '0.5rem' };

// Each category gets an attention-grabbing gradient cover + themed icon.
const CATEGORY_VISUALS = {
  'AI & Development': {
    gradient: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 60%, #ec4899 100%)',
    Icon: BrainCircuit,
  },
  'Business Intelligence': {
    gradient: 'linear-gradient(135deg, #0ea5e9 0%, #2563eb 60%, #1e3a8a 100%)',
    Icon: BarChart3,
  },
  'Digital Marketing': {
    gradient: 'linear-gradient(135deg, #f97316 0%, #ef4444 55%, #db2777 100%)',
    Icon: Megaphone,
  },
  'Career & AI': {
    gradient: 'linear-gradient(135deg, #10b981 0%, #14b8a6 55%, #0891b2 100%)',
    Icon: Rocket,
  },
};

const getVisual = (category) =>
  CATEGORY_VISUALS[category] || {
    gradient: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
    Icon: BookOpen,
  };

// A web image over a themed gradient. Falls back to the gradient + icon if the
// image fails to load, so a card is never a broken image.
const CoverArt = ({ category, image, height = 150, radius = '0.75rem' }) => {
  const { gradient, Icon } = getVisual(category);
  const [imgFailed, setImgFailed] = useState(false);
  const showImage = image && !imgFailed;

  return (
    <div
      style={{
        position: 'relative',
        height,
        borderRadius: radius,
        background: gradient,
        overflow: 'hidden',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      {showImage ? (
        <>
          <img
            src={image}
            alt={category}
            loading="lazy"
            onError={() => setImgFailed(true)}
            style={{
              position: 'absolute',
              inset: 0,
              width: '100%',
              height: '100%',
              objectFit: 'cover',
            }}
          />
          {/* Subtle gradient tint + category icon badge for a consistent look */}
          <div
            style={{
              position: 'absolute',
              inset: 0,
              background:
                'linear-gradient(180deg, rgba(0,0,0,0) 40%, rgba(0,0,0,0.45) 100%)',
            }}
          />
          <div
            style={{
              position: 'absolute',
              top: '0.6rem',
              left: '0.6rem',
              width: '2rem',
              height: '2rem',
              borderRadius: '0.5rem',
              background: gradient,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: '0 2px 6px rgba(0,0,0,0.25)',
            }}
          >
            <Icon size={18} color="#ffffff" strokeWidth={1.8} />
          </div>
        </>
      ) : (
        <>
          <Icon
            size={height * 0.75}
            color="rgba(255,255,255,0.9)"
            strokeWidth={1.4}
            style={{
              position: 'absolute',
              right: '-6%',
              bottom: '-18%',
              opacity: 0.18,
            }}
          />
          <Icon size={height * 0.32} color="#ffffff" strokeWidth={1.6} />
        </>
      )}
    </div>
  );
};

const formatDate = (iso) => {
  try {
    return new Date(iso).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  } catch {
    return iso;
  }
};

const Blog = () => {
  const [selectedPost, setSelectedPost] = useState(null);

  // Lock body scroll and close on Escape while the popup is open.
  useEffect(() => {
    if (!selectedPost) return;

    const onKeyDown = (e) => {
      if (e.key === 'Escape') setSelectedPost(null);
    };
    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', onKeyDown);

    return () => {
      document.body.style.overflow = '';
      window.removeEventListener('keydown', onKeyDown);
    };
  }, [selectedPost]);

  return (
    <div className="page-container">
      <h1 className="page-title">
        <BookOpen size={28} style={iconStyle} />
        Blog
      </h1>

      <p
        style={{
          color: 'var(--text-secondary)',
          lineHeight: '1.7',
          marginBottom: '2.5rem',
          textAlign: 'center',
          maxWidth: '640px',
          margin: '0 auto 2.5rem',
        }}
      >
        Thoughts on AI, Business Intelligence, software engineering, and digital
        marketing in the age of AI. Short, practical reads from my work as a
        software engineer. Click any article to read it.
      </p>

      <div className="projects-grid">
        {blogData.map((post) => (
          <article
            key={post.id}
            className="project-card"
            onClick={() => setSelectedPost(post)}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                setSelectedPost(post);
              }
            }}
            style={{ cursor: 'pointer', display: 'flex', flexDirection: 'column' }}
          >
            <div style={{ marginBottom: '1rem' }}>
              <CoverArt category={post.category} image={post.image} />
            </div>

            <span
              style={{
                display: 'inline-block',
                alignSelf: 'flex-start',
                backgroundColor: 'var(--bg-secondary)',
                color: 'var(--primary-color)',
                border: '1px solid var(--border-color)',
                padding: '0.2rem 0.7rem',
                borderRadius: '1rem',
                fontSize: '0.72rem',
                fontWeight: 600,
                marginBottom: '0.85rem',
                letterSpacing: '0.02em',
              }}
            >
              {post.category}
            </span>

            <h3
              className="project-title"
              style={{ marginBottom: '0.6rem', lineHeight: 1.35 }}
            >
              {post.title}
            </h3>

            {/* Preview clamped to 3 lines */}
            <p
              style={{
                color: 'var(--text-secondary)',
                lineHeight: 1.6,
                fontSize: '0.925rem',
                margin: 0,
                display: '-webkit-box',
                WebkitLineClamp: 3,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
              }}
            >
              {post.excerpt}
            </p>

            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                marginTop: 'auto',
                paddingTop: '1rem',
                fontSize: '0.78rem',
                color: 'var(--text-secondary)',
              }}
            >
              <span
                style={{ display: 'inline-flex', alignItems: 'center', gap: '0.7rem' }}
              >
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.3rem' }}>
                  <Calendar size={13} />
                  {formatDate(post.date)}
                </span>
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.3rem' }}>
                  <Clock size={13} />
                  {post.readTime}
                </span>
              </span>
              <span
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '0.25rem',
                  color: 'var(--primary-color)',
                  fontWeight: 600,
                }}
              >
                Read <ArrowRight size={14} />
              </span>
            </div>
          </article>
        ))}
      </div>

      {/* Article popup */}
      {selectedPost && (
        <div
          onClick={() => setSelectedPost(null)}
          style={{
            position: 'fixed',
            inset: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.75)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            padding: '1.5rem',
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              position: 'relative',
              width: '100%',
              maxWidth: '720px',
              maxHeight: '88vh',
              overflowY: 'auto',
              backgroundColor: 'var(--bg-primary)',
              borderRadius: '1rem',
              border: '1px solid var(--border-color)',
              padding: '2.5rem',
              boxShadow: 'var(--shadow-lg)',
            }}
          >
            <button
              onClick={() => setSelectedPost(null)}
              aria-label="Close"
              style={{
                position: 'absolute',
                top: '1rem',
                right: '1rem',
                background: 'var(--bg-secondary)',
                border: '1px solid var(--border-color)',
                borderRadius: '0.5rem',
                padding: '0.4rem',
                cursor: 'pointer',
                color: 'var(--text-primary)',
                display: 'flex',
              }}
            >
              <X size={20} />
            </button>

            <div style={{ marginBottom: '1.5rem' }}>
              <CoverArt
                category={selectedPost.category}
                image={selectedPost.image}
                height={190}
                radius="0.75rem"
              />
            </div>

            <span
              style={{
                display: 'inline-block',
                backgroundColor: 'var(--bg-secondary)',
                color: 'var(--primary-color)',
                border: '1px solid var(--border-color)',
                padding: '0.2rem 0.7rem',
                borderRadius: '1rem',
                fontSize: '0.72rem',
                fontWeight: 600,
                marginBottom: '1rem',
              }}
            >
              {selectedPost.category}
            </span>

            <h2
              style={{
                fontSize: '1.7rem',
                fontWeight: 700,
                color: 'var(--text-primary)',
                lineHeight: 1.3,
                margin: '0 0 0.75rem',
                paddingRight: '2.5rem',
              }}
            >
              {selectedPost.title}
            </h2>

            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '1rem',
                fontSize: '0.82rem',
                color: 'var(--text-secondary)',
                marginBottom: '1.75rem',
                paddingBottom: '1.25rem',
                borderBottom: '1px solid var(--border-color)',
              }}
            >
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.35rem' }}>
                <Calendar size={14} />
                {formatDate(selectedPost.date)}
              </span>
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.35rem' }}>
                <Clock size={14} />
                {selectedPost.readTime}
              </span>
            </div>

            {selectedPost.content.map((paragraph, idx) => (
              <p
                key={idx}
                style={{
                  color: 'var(--text-secondary)',
                  lineHeight: 1.8,
                  fontSize: '1rem',
                  marginBottom: '1.15rem',
                }}
              >
                {paragraph}
              </p>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default Blog;
